<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: student.full_name,
                        href: meta.links.student_show,
                    },
                    {
                        name: $tc('messages.interventions', 2),
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div>
                        <ActivityMenuOptions>
                            <InertiaLink
                                v-for="otherStudent in meta.links.otherStudents"
                                :href="otherStudent.url"
                                :key="otherStudent.url"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ otherStudent.text }}
                                <span v-if="otherStudent.quantity">({{ otherStudent.quantity }})</span>
                            </InertiaLink>
                        </ActivityMenuOptions>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <div class="w-full flex space-x-4 justify-end" v-if="missingInterventions.length > 0">
            <AppButton :href="meta.links.create" element="inertia-link">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <EmptyState picture v-if="interventions.length === 0" />

        <Tabs v-else>
            <Tab :title="intervention.type" v-for="intervention in interventions" :key="intervention.id">
                <div class="w-full flex space-x-4">
                    <AppButton :href="intervention.links.edit" element="inertia-link">
                        {{ $t('messages.edit') }}
                    </AppButton>
                </div>

                <div class="mt-5 md:grid md:grid-cols-5 lg:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t('messages.general_information') }}
                        </h3>
                        <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-4 lg:col-span-2">
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                            <div class="sm:col-span-3">
                                <label
                                    for="evaluation_request_date"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    {{ $t('messages.evaluation_request_date') }}
                                </label>
                                <div class="mt-1">
                                    {{ intervention.evaluation_request_date || $t('messages.n/a') }}
                                </div>
                            </div>

                            <div class="sm:col-span-3">
                                <label for="evaluation_date" class="block text-sm font-medium leading-5 text-gray-700">
                                    {{ $t('messages.evaluation_date') }}
                                </label>
                                <div class="mt-1">
                                    {{ intervention.evaluation_date || $t('messages.n/a') }}
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label
                                    for="has_internet_access"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    {{ $t('messages.monitoring') }}
                                </label>
                                <div class="mt-1">
                                    <YesNoNotApplicable :value="intervention.monitoring" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-5 md:grid md:grid-cols-5 lg:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t('messages.assessment') }}
                        </h3>
                        <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-4 lg:col-span-2">
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                            <div class="sm:col-span-2">
                                <label for="intervention" class="block text-sm font-medium leading-5 text-gray-700">
                                    {{ $t('messages.assessment') }}
                                </label>
                                <div class="mt-1">
                                    <YesNoNotApplicable :value="intervention.assessment" />
                                </div>
                            </div>
                            <div class="sm:col-span-6">
                                <label
                                    for="intervention_comment"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    {{ $t('messages.comment') }}: {{ $t('messages.assessment') }}
                                </label>
                                <div class="mt-1">
                                    <p>
                                        {{ intervention.assessment_comment || $t('messages.n/a') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-5 md:grid md:grid-cols-5 lg:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t('messages.report') }}
                        </h3>
                        <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-4 lg:col-span-2">
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                            <div class="sm:col-span-2">&nbsp;</div>
                            <div class="sm:col-span-6">
                                <label
                                    for="intervention_comment"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    {{ $t('messages.comment') }}: {{ $t('messages.report') }}
                                </label>
                                <div class="mt-1">
                                    <p>
                                        {{ intervention.report_comment || $t('messages.n/a') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="mt-5 md:grid md:grid-cols-5 lg:grid-cols-3 md:gap-6">
                    <div class="md:col-span-1">
                        <h3 class="text-lg font-medium leading-6 text-gray-900">
                            {{ $t('messages.interventions') }}
                        </h3>
                        <p class="mt-1 text-sm leading-5 text-gray-500">&nbsp;</p>
                    </div>
                    <div class="mt-5 md:mt-0 md:col-span-4 lg:col-span-2">
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-8">
                            <div class="sm:col-span-2">
                                <label for="intervention" class="block text-sm font-medium leading-5 text-gray-700">
                                    {{ $t('messages.interventions') }}
                                </label>
                                <div class="mt-1">
                                    <YesNoNotApplicable :value="intervention.intervention" />
                                </div>
                            </div>
                            <div class="sm:col-span-6">
                                <label
                                    for="intervention_comment"
                                    class="block text-sm font-medium leading-5 text-gray-700"
                                >
                                    {{ $t('messages.comment') }}: {{ $t('messages.interventions') }}
                                </label>
                                <div class="mt-1">
                                    {{ intervention.intervention_comment || $t('messages.n/a') }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Tab>
        </Tabs>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Tabs from '@/components/Tabs.vue';
import Tab from '@/components/Tab.vue';
import YesNoNotApplicable from '@/components/YesNoNotApplicable.vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';

export default {
    components: {
        PanelPage,
        AppButton,
        EmptyState,
        Tabs,
        Tab,
        YesNoNotApplicable,
        ActivityMenuOptions,
    },
    layout: Layout,
    name: 'Index',
    props: ['meta', 'interventions', 'filters', 'student', 'missingInterventions'],
};
</script>
